/**
 * Shared UI primitives used across the dropship tabs.
 *
 * Extracted from the once-monolithic `SellerDropship.jsx` in the
 * Phase 5 physical refactor. Consumed by tabs (Analytics, Invoices,
 * Claims, Terms, Catalog Sync, Daily Orders, Fulfillment Center)
 * and their sub-panels.
 */
import { useEffect, useState } from "react";
import { Check, ChevronRight } from "lucide-react";

// ────────────────────────────────────────────────────────────────────────
// SectionCard — collapsible, storage-persistable card container.
// ────────────────────────────────────────────────────────────────────────
export function SectionCard({ icon: Icon, title, subtitle, right, children, testId, collapsible = true, defaultOpen = true, storageKey, forceCollapseSignal }) {
  const key = storageKey || (testId ? `setup-section-open:${testId}` : null);
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    if (!key) return defaultOpen;
    try {
      const v = localStorage.getItem(key);
      return v == null ? defaultOpen : v === "1";
    } catch { return defaultOpen; }
  });
  // Auto-collapse when parent bumps `forceCollapseSignal` (used after successful save).
  useEffect(() => {
    if (forceCollapseSignal && open) {
      setOpen(false);
      if (key) { try { localStorage.setItem(key, "0"); } catch { /* ignore */ } }
    }
  }, [forceCollapseSignal]);
  const toggle = () => {
    if (!collapsible) return;
    setOpen(v => {
      const next = !v;
      if (key) { try { localStorage.setItem(key, next ? "1" : "0"); } catch { /* ignore */ } }
      return next;
    });
  };
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid={testId}>
      <div className={`flex items-center gap-3 px-5 py-4 ${open ? "border-b border-border" : ""} ${collapsible ? "cursor-pointer hover:bg-secondary/30" : ""}`}
           onClick={collapsible ? toggle : undefined}
           data-testid={testId ? `${testId}-header` : undefined}>
        {Icon && <Icon className="w-4 h-4 text-foreground/60 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold">{title}</div>
          {subtitle && open && <div className="text-[11.5px] text-foreground/55 mt-0.5">{subtitle}</div>}
        </div>
        {right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
        {collapsible && (
          <ChevronRight className={`w-4 h-4 text-foreground/40 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
        )}
      </div>
      {open && children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// MiniStat — compact label+value+hint tile used inside SectionCard.
// ────────────────────────────────────────────────────────────────────────
export function MiniStat({ label, value, hint }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/55">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{value}</div>
      {hint && <div className="text-[11px] text-foreground/50 mt-0.5">{hint}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// FeeRow — configurable fee (handling / rush / insurance) with toggle.
// ────────────────────────────────────────────────────────────────────────
export function FeeRow({ label, hint, cfg, onChange, unit = "$" }) {
  return (
    <div className="grid grid-cols-[1fr_auto_140px] items-center gap-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[13px] font-medium">{label}</div>
        <div className="text-[11px] text-foreground/55 mt-0.5">{hint}</div>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={cfg.on}
          onChange={e => onChange({ ...cfg, on: e.target.checked })}
          className="w-4 h-4 rounded border-border accent-foreground"
        />
        <span className="text-[11.5px] text-foreground/70">{cfg.on ? "Enabled" : "Optional"}</span>
      </label>
      <div className={`flex items-center rounded-md border transition-colors ${cfg.on ? "border-border bg-white" : "border-dashed border-border/60 bg-secondary/40 opacity-60"}`}>
        <input
          type="number"
          step="0.10"
          min="0"
          disabled={!cfg.on}
          value={cfg.value}
          onChange={e => onChange({ ...cfg, value: Number(e.target.value) })}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm bg-transparent outline-none disabled:cursor-not-allowed"
        />
        <span className="px-2 text-[11px] text-foreground/60 border-l border-border/60">{unit}/order</span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// ScopedFeeSummary — inline preview of a saved fee config.
// ────────────────────────────────────────────────────────────────────────
export function ScopedFeeSummary({ label, cfg }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="text-[13px] text-foreground/80">{label}</div>
      <div className="text-[12px] font-medium tabular-nums text-foreground/70">
        {cfg.on ? `$${Number(cfg.value).toFixed(2)} / order` : "Off"}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// ModeCard — "Model A vs Model B" style pick card with feature list.
// ────────────────────────────────────────────────────────────────────────
export function ModeCard({ active, onPick, icon: Icon, title, tagline, features, requires }) {
  return (
    <button
      onClick={onPick}
      className={`text-left rounded-2xl border p-5 transition-all ${active ? "border-foreground bg-foreground text-background" : "border-border bg-white hover:border-foreground/40"}`}
      data-testid={`mode-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <Icon className="w-5 h-5"/>
        {active && <span className="text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-background/20">Selected</span>}
      </div>
      <div className="display text-2xl mt-3">{title}</div>
      <div className={`text-[11.5px] mt-1 ${active ? "text-background/70" : "text-foreground/55"}`}>{tagline}</div>
      <ul className="mt-4 space-y-1.5 text-[12.5px]">
        {features.map((f, i) => <li key={i} className="flex items-start gap-1.5"><Check className={`w-3 h-3 mt-1 shrink-0 ${active ? "text-emerald-300" : "text-emerald-600"}`}/>{f}</li>)}
      </ul>
      <div className={`mt-3 pt-3 border-t text-[11.5px] ${active ? "border-background/20 text-background/70" : "border-border text-foreground/55"}`}>
        <strong className="block mb-1">Requires</strong>
        {requires.map((r, i) => <div key={i}>• {r}</div>)}
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────
// SectionHeading — page-level section title with optional action slot.
// ────────────────────────────────────────────────────────────────────────
export function SectionHeading({ title, sub, action }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h3 className="display text-xl tracking-tight">{title}</h3>
        {sub && <p className="text-foreground/55 text-[12.5px] mt-0.5 max-w-2xl">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// MetricCard — big-KPI tile with optional tone (rose/amber/emerald) and icon.
// ────────────────────────────────────────────────────────────────────────
export function MetricCard({ label, value, tone, icon: Icon }) {
  const t = tone === "emerald" ? "border-emerald-200 bg-emerald-50" : tone === "amber" ? "border-amber-200 bg-amber-50" : tone === "rose" ? "border-rose-200 bg-rose-50" : "border-border bg-white";
  return (
    <div className={`rounded-2xl border p-4 ${t}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/40"/>}
      </div>
      <div className="display text-2xl mt-1">{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Stat — compact label/value pair used in agreement summaries.
// ────────────────────────────────────────────────────────────────────────
export function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">{label}</div>
      <div className="display text-lg mt-0.5">{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// KpiTile — big-value KPI card (larger than MetricCard, no icon slot).
// ────────────────────────────────────────────────────────────────────────
export function KpiTile({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="display text-3xl mt-1.5">{value}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1">{hint}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// EmptyState — big dashed-border empty state card with icon + title + body.
// ────────────────────────────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-12 text-center">
      <div className="w-14 h-14 rounded-2xl bg-white border border-border grid place-items-center mx-auto">
        <Icon className="w-6 h-6 text-foreground/40"/>
      </div>
      <div className="display text-xl mt-4">{title}</div>
      <p className="text-sm text-foreground/55 mt-1.5 max-w-md mx-auto">{body}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// FormField — labeled form field wrapper with optional hint + required badge.
// ────────────────────────────────────────────────────────────────────────
export function FormField({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-xs mono uppercase tracking-[0.18em] text-foreground/55 block">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-foreground/55">{hint}</div>}
    </div>
  );
}
